import { usegetConditionHistory } from "@features/ax/hooks";
import { GenericTable } from "@shared/components/GenericTable";
import { PageHeader } from "@shared/components/PageHeader";
import { TableColumn } from "@shared/types/table";
import React, { useMemo, useState } from "react";
import { useParams } from "react-router-dom";

interface AssessmentRecord {
  id: string;
  date: string;
  assessmentDate: string;
  scoreType: string;
  score: number;
  status: string;
  assessedBy: string;
  notes?: string;
}

interface AssessmentHistoryProps {
  onViewDetails?: (record: AssessmentRecord) => void;
}

const getStatusBadgeClass = (status: string): string => {
  switch (status.toLowerCase()) {
    case "excellent":
    case "good":
      return "bg-green-100 text-green-800";
    case "fair":
    case "medium":
      return "bg-yellow-100 text-yellow-800";
    case "poor":
    case "high":
      return "bg-red-100 text-red-800";
    case "critical":
      return "bg-red-100 text-red-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
};

export const AssessmentHistory: React.FC<AssessmentHistoryProps> = ({
  onViewDetails,
}) => {
  const { id } = useParams();
  const facilityEntityType = 5;

  const [filters, setFilters] = useState({
    search: "",
    scoreType: "all",
    assessedBy: "all",
    status: "all",
    startDate: "",
    endDate: "",
    page: 1,
  });
  const PAGE_SIZE = 10;

  const { data: conditionHistory } = usegetConditionHistory(
    Number(id),
    facilityEntityType,
    filters.page
  );

  const columns: TableColumn<any>[] = useMemo(
    () => [
      {
        key: "assessmentTypeDisplay",
        header: "Score Type",
        type: "text",
        render: (value: string) => (
          <span className="font-medium ml-4">{value}</span>
        ),
      },
      {
        key: "score",
        header: "Score",
        type: "custom",
        render: (value: number, row: any) => (
          <span className="font-medium ml-4">
            {row.assessmentTypeDisplay === "Risk"
              ? row.riskScore
              : row.conditionScore}
          </span>
        ),
      },
      {
        key: "statusDisplay",
        header: "Status",
        type: "badge",
        badgeConfig: {
          getVariant: () => "secondary",
          getClassName: (value: string) => getStatusBadgeClass(value),
        },
        render: (value: string) => (
          <span
            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeClass(
              value
            )}`}
          >
            {value}
          </span>
        ),
      },
      {
        key: "createdDate",
        header: "Assessment Date",
        type: "text",
      },
      {
        key: "createdUserRemoteName",
        header: "Assessed By",
        type: "text",
        render: (value: string) => (
          <span className="font-medium ml-4">{value}</span>
        ),
      },
    ],
    []
  );

  const handlePageChange = (page: number) => {
    setFilters((prev) => ({ ...prev, page }));
  };

  // Pagination logic
  const totalItems = conditionHistory?.count;
  const totalPages = Math.ceil(totalItems / PAGE_SIZE);
  const startIndex = (filters.page - 1) * PAGE_SIZE;

  const pagination = useMemo(
    () => ({
      currentPage: filters.page,
      totalPages,
      totalItems,
      itemsPerPage: PAGE_SIZE,
      startItem: startIndex + 1,
      endItem: Math.min(filters.page * PAGE_SIZE, totalItems),
    }),
    [filters.page, totalPages, totalItems, startIndex]
  );

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="flex items-center justify-between">
        <PageHeader
          title="Assessment History"
          subtitle="Complete history of all condition and risk assessments"
        />
      </div>

      {/* Show message if no data */}
      {conditionHistory?.results.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          No assessment history available
        </div>
      ) : (
        /* Generic Table with integrated filter panel */
        <GenericTable
          data={conditionHistory?.results}
          columns={columns}
          rowKey="id"
          pagination={pagination}
          onPageChange={handlePageChange}
          actionsType="buttons"
        />
      )}
    </div>
  );
};
